import { useState, useEffect, useCallback } from 'react'
import { caseService } from '@/services/case-service'
import type { CaseDocument } from '@/types/case'
import { UnifiedDocumentManager } from './documents/UnifiedDocumentManager'
import { CASE_DOCUMENT_CONFIG } from './documents/configs/case-config'
import { UnifiedDocument, DocumentUploadData } from './documents/types/document-types'

import { getErrorMessage } from '../utils/error.utils'
interface CaseDocumentsProps {
  caseId: string
  caseName?: string
  onDocumentUploaded?: () => void
  onDocumentDeleted?: (documentId: string) => void
  readonly?: boolean
}

// Tipos gravados pela API (rótulos em português) ↔ chaves da configuração da tela
const LEGACY_TYPE_KEYS: Record<string, string> = {
  'Petição': 'PETITION',
  'Contrato': 'CONTRACT',
  'Evidência': 'EVIDENCE',
  'Decisão Judicial': 'COURT_DECISION',
  'Correspondência': 'CORRESPONDENCE',
  'Foto': 'PHOTO',
  'Vídeo': 'VIDEO',
  'Áudio': 'AUDIO',
  'Relatório Médico': 'MEDICAL_REPORT',
  'Documento Financeiro': 'FINANCIAL_DOCUMENT',
  'Outro': 'OTHER'
}

const LEGACY_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  Object.entries(LEGACY_TYPE_KEYS).map(([label, key]) => [key, label])
)

/**
 * A API devolve o nome original em `fileName`, a data em `uploadedAt` e a
 * finalidade em `description`; a lista trabalha com UnifiedDocument.
 */
export function toUnifiedCaseDocument(doc: CaseDocument, caseId: string): UnifiedDocument {
  return {
    id: doc.id,
    entityType: 'case',
    entityId: caseId,
    title: doc.fileName,
    fileName: doc.fileName,
    filePath: doc.filePath,
    fileSize: doc.fileSize,
    mimeType: doc.mimeType,
    documentType: LEGACY_TYPE_KEYS[doc.documentType] || doc.documentType || 'OTHER',
    description: doc.description ?? undefined,
    isConfidential: Boolean(doc.metadata?.isConfidential),
    uploadedBy: doc.uploadedBy,
    uploadedByName: doc.uploadedByName,
    createdAt: doc.uploadedAt,
    updatedAt: doc.uploadedAt
  }
}

export function CaseDocuments({
  caseId,
  caseName,
  onDocumentUploaded,
  onDocumentDeleted,
  readonly = false
}: CaseDocumentsProps) {
  const [documents, setDocuments] = useState<UnifiedDocument[]>([])
  const [loading, setLoading] = useState(true)

  const loadDocuments = useCallback(async () => {
    try {
      setLoading(true)
      const docs = await caseService.getCaseDocuments(caseId)

      // Check if docs is an array
      if (!Array.isArray(docs)) {
        console.error('Documents response is not an array:', docs)
        setDocuments([])
        return
      }

      setDocuments(docs.map(doc => toUnifiedCaseDocument(doc, caseId)))
    } catch (err) {
      console.error('Error loading case documents:', err)
      setDocuments([])
    } finally {
      setLoading(false)
    }
  }, [caseId])

  useEffect(() => {
    loadDocuments()
  }, [loadDocuments])

  const handleUpload = async (data: DocumentUploadData): Promise<void> => {
    try {
      setLoading(true)

      // O nome do arquivo segue o original; a descrição é opcional
      await caseService.uploadDocument(
        caseId,
        data.file,
        LEGACY_TYPE_LABELS[data.documentType] || 'Outro',
        data.isConfidential,
        data.description
      )

      // Reload documents from backend
      await loadDocuments()
      onDocumentUploaded?.()

    } catch (err: unknown) {
      throw new Error(getErrorMessage(err, 'Erro ao fazer upload do documento'))
    } finally {
      setLoading(false)
    }
  }

  const handleDownload = async (document: UnifiedDocument): Promise<void> => {
    try {
      const blob = await caseService.downloadDocument(caseId, document.id)

      const url = window.URL.createObjectURL(blob)
      const link = window.document.createElement('a')
      link.href = url
      link.download = document.fileName || document.title
      link.style.display = 'none'

      // Add to DOM, click, then remove
      window.document.body.appendChild(link)
      link.click()

      // Cleanup after a short delay
      setTimeout(() => {
        window.document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      }, 100)
    } catch (err) {
      console.error('Erro no download:', err)
      throw new Error('Erro ao baixar documento')
    }
  }

  const handlePreview = (document: UnifiedDocument): Promise<Blob> => {
    return caseService.previewDocument(caseId, document.id)
  }

  const handleDelete = async (documentId: string): Promise<void> => {
    try {
      await caseService.deleteDocument(caseId, documentId)
      setDocuments(documents.filter(doc => doc.id !== documentId))
      onDocumentDeleted?.(documentId)
    } catch (err) {
      throw new Error('Erro ao excluir documento')
    }
  }

  const handleUpdateDescription = async (document: UnifiedDocument, description: string | null): Promise<void> => {
    const updated = await caseService.updateDocumentDescription(caseId, document.id, description)
    setDocuments(docs => docs.map(doc => (doc.id === document.id ? { ...doc, description: updated?.description ?? undefined } : doc)))
  }

  return (
    <UnifiedDocumentManager
      config={readonly ? { ...CASE_DOCUMENT_CONFIG, ui: { ...CASE_DOCUMENT_CONFIG.ui, modalMode: false } } : CASE_DOCUMENT_CONFIG}
      entityId={caseId}
      entityName={caseName}
      documents={documents}
      loading={loading}
      onUpload={handleUpload}
      onDelete={handleDelete}
      onDownload={handleDownload}
      onPreview={handlePreview}
      onUpdateDescription={handleUpdateDescription}
      className={readonly ? 'readonly-mode' : ''}
    />
  )
}
